import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable, tap, map } from 'rxjs';
import { RoutePageComponent } from '../../shared/route-page/route-page.component';
import { Field, FieldControlService, IField, ChoiceChipComponent, InputRadioComponent, InputToggleComponent } from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { InformationCardComponent } from '../../shared/information-card/information-card.component';
import { DataStoreService } from '../../../services/data-store.service';
import { IPage } from '../../../services/base/page';
import { PaymentService } from '../../../services/payment-portal.service';
import { PaymentNavigationService } from '../../../services/payment-navigation.service';
import { NavigationFooterComponent } from '../../shared/navigation-footer/navigation-footer.component';
import { ActivatedRoute, Router } from '@angular/router';
import { FooterImageComponent } from '../../shared/footer-image/footer-image.component';
import { ModalModule } from 'ngx-bootstrap/modal';
import { AccountDetails, IPayload, PolicyDetails, UIResponse } from '../../../services/base/policy-details';
import { LoadingService } from '../../../services/loading.service';
import { LogMessageModel } from '../../shared/log-message.model';
import { globalConstants } from '../../shared/global-constants';
import { ToastService } from '../../../services/toast.service';
import { ModalService } from '../../../services/modal.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CCPaymentConfirmationComponent } from '../../shared/cc-payment-confirmation/cc-payment-confirmation.component';
import { ProductNamePipe } from '../../pipes/productName.pipe';
import { CryptoService } from '../../../services/crypto.service';
import { APP_CONFIG, AppConfig } from '../../../../app.config';

declare let OneInc: any;

@Component({
    selector: 'app-make-payment',
    imports: [
        CommonModule,
        RoutePageComponent,
        ReactiveFormsModule,
        FormsModule,
        MatChipsModule,
        InformationCardComponent,
        NavigationFooterComponent,
        FooterImageComponent,
        ModalModule,
        ChoiceChipComponent,
        InputRadioComponent,
        InputToggleComponent
    ],
    templateUrl: './make-payment.component.html',
    styleUrl: './make-payment.component.scss',
    providers: [FieldControlService]
})
export class MakePaymentComponent implements OnInit {
    page: IPage;
    isMobile = toSignal(
        this.breakpointObserver.observe(['(max-width: 768px)']).pipe(map(r => r.matches)),
        { initialValue: this.breakpointObserver.isMatched('(max-width: 768px)') }
    );
    amountSelected: any;
    selectedPaymentMethod: any;
    policyData: PolicyDetails;
    policyDataField: IField;
    paymentAmountField: IField;
    paymentMethodField: IField;
    eftPaymentMethodField: IField;
    paymentDetails: IPayload | null;
    amount: any;
    accountDetail: AccountDetails;
    otherPaymentSelected = signal(false);
    paymentMethod = signal('');
    showOneIncLogo = signal(false);
    private portalOne: any;
    proxy: any;
    showNamedInsured = signal(false);
    namedInsuredField: IField = new Field(
        {
            name: "namedInsuredField",
            description: "<span class='caption-bold'>Does the Card belong to the name insured?<span>",
            disabled: false,
            id: "namedInsuredField",
            type: "toggle_switch",
            value: false,
            hintText: "yes"
        }
    )

    showProcessingFee = signal(false);
    logmsg: LogMessageModel = new LogMessageModel()
    showInvalidInfoDialog = signal(false);
    isReadyToSave = signal(false);
    proceedpayFlag = signal(false);
    eftpaymentForm: UntypedFormGroup;
    paymentOptionForm: UntypedFormGroup;
    paymentMethodTypeForm: UntypedFormGroup;
    loaded = signal(false);
    loadedFromQueryParams = signal(false);
    processingFeeResponse: any = {};
    private dialogRef: MatDialogRef<CCPaymentConfirmationComponent, any>;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        public dialog: MatDialog,
        public datePipe: DatePipe,
        private cdr: ChangeDetectorRef,
        private crypto: CryptoService,
        private loader: LoadingService,
        private toastService: ToastService,
        private modalService: ModalService,
        private dataStore: DataStoreService,
        private paymentService: PaymentService,
        private paymentNavigationService: PaymentNavigationService,
        private fieldService: FieldControlService,
        private breakpointObserver: BreakpointObserver,
        @Inject(APP_CONFIG) private config: AppConfig
    ) {
    }

    ngOnInit(): void {
        this.route.queryParams.subscribe((query) => {
            if (query?.['Category'] && query?.['search'] && query?.['Zipcode']) {
                // Direct URL access with query params — call the service to fetch policy data first
                this.loadedFromQueryParams.set(true);
                this.paymentNavigationService.setNavigationState({
                    category: query['Category'],
                    search: query['search'],
                    zipcode: query['Zipcode']
                });
                this.retrieveAndLoadPage(query['Category'], query['search'], query['Zipcode']);
            } else {
                // Normal navigation — payment data already available in PaymentService
                this.loadPage();
            }
        });
    }

    private retrieveAndLoadPage(category: string, search: string, zipcode: string): void {
        let paymentDetailsEncryptPath = this.config.paymentDetailsEncryptPath || '';
        paymentDetailsEncryptPath = paymentDetailsEncryptPath.replace('{category}', category);
        paymentDetailsEncryptPath = paymentDetailsEncryptPath.replace('{reference}', search);
        paymentDetailsEncryptPath = paymentDetailsEncryptPath.replace('{zip}', zipcode);

        const payload: IPayload = {} as IPayload;
        payload.encryptedData = encodeURIComponent(
            this.crypto.encryptData(paymentDetailsEncryptPath, this.config.encryptKeyGPP)
        );

        this.paymentService.callPaymentService(payload, globalConstants.policyRetrievalService).subscribe((res: UIResponse) => {
            this.paymentDetails = this.paymentService.getPaymentDetails();
            if (res.statusCode === 200 && res.payload?.retrievePolicyResponse?.statusCode === 200) {
                if(this.paymentDetails?.retrievePolicyResponse?.details.accountDetail.disablePayment 
                    || Number(this.paymentDetails?.retrievePolicyResponse?.details.accountDetail.outstandingBalance) === 0){
                    this.router.navigate(['/payment-center']);
                } else {
                    this.loadPage();   
                }               
            }else {
                this.toastService.add({
                    type: 'error',
                    title: 'Error',
                    message: 'The information provided did not match our records. Please review it and try again',
                });
                this.router.navigate(['/paymentportal']);
            }
        });
    }

    private loadPage(): void {
        this.dataStore.getPageData('payment-center').subscribe((pageData) => {
            this.paymentDetails = this.paymentService.getPaymentDetails();
            this.policyData = this.paymentDetails?.retrievePolicyResponse?.details.policyDetails?.at(0) || {} as PolicyDetails;
            this.accountDetail = this.paymentDetails?.retrievePolicyResponse?.details.accountDetail || {} as AccountDetails;
            this.page = pageData;
            this.policyDataField = new Field(pageData.getField('policyDetails'));
            this.paymentAmountField = new Field(pageData.getField('paymentAmount'));
            this.paymentMethodField = new Field(pageData.getField('paymentMethod'));
            this.pagePreProcess();
            this.generateForm();
            this.loaded.set(true);
            this.cdr.markForCheck();
        });
    }

    pagePreProcess(): void {
        this.policyDataField.caption = new ProductNamePipe().transform(this.policyData?.lob || '')
        this.policyDataField.icon_path = 'products/' + this.policyDataField.caption;
        this.policyDataField.description = this.policyData?.insuredAddress + ', ' + this.policyData?.insuredCity + ', ' + this.policyData?.insuredState + ' ' + this.policyData?.insuredZip;

        this.updatePolicyDataField();

        if (Number(this.accountDetail?.amountDue) === 0.00) {
            this.paymentAmountField.getOption('AmountDueField')!.disabled = true;
        }
        this.paymentAmountField.getOption('AmountDueField')!.getOption('AmountDue')!.value = `$${Number(this.accountDetail?.amountDue).toFixed(2)}`;
        this.paymentAmountField.getOption('BalanceDueField')!.getOption('BalanceDue')!.value = `$${Number(this.accountDetail?.outstandingBalance).toFixed(2)}`;

        if (Number(this.accountDetail?.amountDue) != Number(this.accountDetail?.outstandingBalance)) {
            this.paymentAmountField.getOption('OtherDueField')!.disabled = false;
            let otherAmountField = this.paymentAmountField.getOption('OtherAmountField')!;
            otherAmountField.min = this.accountDetail?.amountDue || 0;
            otherAmountField.max = this.accountDetail?.outstandingBalance || 0;
            otherAmountField.hintText = `From $${this.accountDetail?.amountDue} to $${this.accountDetail?.outstandingBalance}`;
        }

        if (this.accountDetail.eftMethod === 'Manual') {
            this.eftPaymentMethodField = this.page.getField('eftPaymentMethod')!
        }
    }

    updatePolicyDataField() {
        let termTemplate = this.policyDataField.options.optionsList[0];
        this.policyDataField.options.optionsList = [];
        this.paymentDetails?.retrievePolicyResponse?.details.policyDetails.forEach((policy: PolicyDetails) => {
            let termField = new Field({
                name: `Term-${policy.term}`,
                options: {
                    optionsList: termTemplate.options.optionsList
                }
            })

            termField.options.optionsList.forEach((displayField: any) => {
                const fieldName = displayField.name;

                if (fieldName && policy.hasOwnProperty(fieldName)) {
                    const key = fieldName as keyof PolicyDetails;
                    const value = policy[key];
                    displayField.value = value !== null && value !== undefined && value !== '' ? value : '-';
                } else if (fieldName.includes('policyStatusTag')) {
                    displayField.value = policy.tag !== '' ? policy.tag : policy.policyStatus;
                } else if (fieldName.includes('termPeriod')) {
                    displayField.value =
                        policy?.effectiveDate && policy?.expirationDate
                            ? `${this.datePipe.transform(policy?.effectiveDate, 'MM/dd/yyyy')} - ${this.datePipe.transform(policy?.expirationDate, 'MM/dd/yyyy')}` : '-';
                } else if (fieldName.includes('dueDate')) {
                    displayField.value = policy?.paymentDue ? `${this.datePipe.transform(policy?.paymentDue, 'MM/dd/yyyy')}` : '-';
                } else {
                    displayField.value = '-';
                }

                if (displayField.value !== '-' && ['amountDue', 'outstandingBalance'].includes(fieldName)) {
                    displayField.value = `$${Number(displayField.value).toFixed(2)}`;
                }
            });

            this.policyDataField.options.optionsList.push(termField);
        });
    }

    generateForm(): void {
        this.paymentOptionForm = this.fieldService.toFormGroup(this.paymentAmountField.options.optionsList);
        if (this.accountDetail.eftMethod === 'Manual') {
            this.eftpaymentForm = new FormGroup({
                eftMethodType: new FormControl('Checking', [Validators.required]),
                routingNumber: new FormControl('', [Validators.required, Validators.minLength(9), Validators.maxLength(9)]),
                accountNumber: new FormControl('', [Validators.required, Validators.maxLength(20)]),
                accountHolderName: new FormControl('', [Validators.required, Validators.minLength(4), Validators.maxLength(50), Validators.pattern('^[a-zA-Z ]*$')])
            })
        }
        if (this.accountDetail?.disablePayment) {
            this.paymentAmountField.getOption('AmountDueField')!.disabled = true;
            this.paymentAmountField.getOption('BalanceDueField')!.disabled = true;
            this.paymentAmountField.getOption('OtherDueField')!.disabled = true;
        }
    }

    get f() {
        return this.eftpaymentForm?.controls || {};
    }

    updateSelectedField(event: any): void {
        if (event) {
            if (this.accountDetail.amountDue === this.accountDetail.outstandingBalance) {
                this.paymentOptionForm.get('AmountDueField')?.setValue(this.paymentOptionForm.value?.selectedAmtToPay, { emitEvent: false })
                this.paymentOptionForm.get('BalanceDueField')?.setValue(this.paymentOptionForm.value?.selectedAmtToPay, { emitEvent: false })
            }
            this.getProcessingFee().subscribe();
            this.paymentMethodField.disabled = false;
        } else {
            if (this.paymentOptionForm.value?.paymentAmountType === 'Other Due') {
                this.processingFeeResponse!.convenienceFee = '0.00'
            }
            this.paymentMethodField.disabled = true;
        }
    }

    showOneIncPaymentWidget(event: any) {
        this.paymentMethod.set(event);
        let ele = document.getElementById('portalOneContainer');
        while (ele?.firstChild) {
            ele.removeChild(ele.firstChild);
        }
        if (event === 'CreditCard') {
            this.eftpaymentForm?.reset();
            if (this.processingFeeResponse!.eligible === true) {
                this.openPaymentModal(event);
            } else {
                this.initiateOneIceSession()
            }
        } else if (event === 'ECheck' && this.accountDetail.eftMethod === 'OneInc') {
            this.resetPaymentInformation();
            this.eftpaymentForm?.reset();
            this.initiateOneIceSession();
        } else if (event === 'ECheck' && this.accountDetail.eftMethod === 'Manual') {
            this.resetPaymentInformation();
        }
    }

    initiateOneIceSession() {
        let payload: IPayload = {} as IPayload;
        let policyDetails = this.paymentDetails?.retrievePolicyResponse?.details.policyDetails?.at(0);
        payload.quoteId = policyDetails?.policyNumber;
        this.paymentService.callPaymentService(payload, globalConstants.createOneIncSessionService).subscribe(
            (res: UIResponse) => {
                if (res.payload.oneincSessionResponse?.statusCode === 200) {
                    const container = document.getElementById('portalOneContainer');
                    this.portalOne = new OneInc.PortalOne(container, this.subscribeToEvents.bind(this), null);
                    this.getCustomerId(res.payload.oneincSessionResponse?.portalOneSessionKey || '');
                } else {
                    this.toastService.add({
                        type: 'error',
                        title: 'Error',
                        message: 'Failed to initiate payment session. Please try again later.',
                    });
                }
            }
        );
    }

    getCustomerId(sessionID: string) {
        let request = {
            quoteid: this.policyData.policyNumber,
            action: 'account',
            customerId: this.policyData.policyNumber,
            customerName: '',
        };
        this.paymentService.getPaymentData(request).subscribe((response) => {
            const customerId = response.customerId;
            if (this.paymentMethod() == 'CreditCard') {
                this.showNamedInsured.set(true);
            }
            this.openModalWindow(sessionID, customerId);
        });
    }

    private openModalWindow(sessionKey: string, customerId: string): void {
        const options = {
            onSuccess: (result: any) => {
                this.payCharge(result);
            },
            onFail: () => {
                this.isReadyToSave.set(false);
                this.proceedpayFlag.set(false);
                this.paymentMethodFailError();
            },
            onValidationComplete: (isReadyToSave: any) => {
                this.isReadyToSave.set(isReadyToSave);
                this.proceedpayFlag.set(isReadyToSave);
            },
        };
        let savePaymentRequest = {
            paymentCategory: this.paymentMethod(),
            feeContext: 'PaymentWithFee',
            minAmountDue: this.paymentOptionForm.value?.selectedAmtToPay.replace('$', ''),
            billingZip: '',
            billingAddressStreet: '',
            policyHolderName: '',
            referenceNumber: this.policyData.policyNumber,
            sessionId: sessionKey,
            displayMode: 'inline',
            interactionMode: 'ExternalExchange',
            customerId: customerId,
        };
        this.proxy = this.portalOne.savePaymentMethod(savePaymentRequest, options);
        this.showOneIncLogo.set(true);
        this.loader.hideLoading();
    }

    private subscribeToEvents(container: HTMLElement): void {
        if (!container) {
            return;
        }
        container.addEventListener('portalOne.load', () => {
            this.loader.hideLoading();
        });

        container.addEventListener('portalOne.error', (event: any) => {
            this.resetPaymentInformation()
            this.toastService.add({
                type: 'error',
                title: 'Error',
                message: event.detail?.Description,
            });
        });
    }

    paymentMethodFailError(): void {
        this.resetPaymentInformation();
        this.toastService.add({
            type: 'error',
            title: 'Error',
            message: 'There is an issue with your payment method. Please re-enter the information or choose a different payment method.',
        });
        let ele = document.getElementById('portalOneContainer');
        while (ele?.firstChild) {
            ele.removeChild(ele.firstChild);
        }
    }

    payCharge(data: any) {
        let policyDetails = this.paymentDetails?.retrievePolicyResponse?.details.policyDetails?.at(0);
        const reqParam = {
            "acctorpolicynumber": this.accountDetail?.policySource === 'Legacy' ? this.accountDetail?.legacyInPolicy : this.accountDetail?.accountNumber,
            "acknowledgmentRequired": "",
            "agentaddress": "",
            "agentcity": "",
            "agentname": "",
            "agentphone": "",
            "agentreferences": "",
            "agentstate": "",
            "agentzip": "",
            "bankaccttype": "",
            "bankroutingnumber": "",
            "batchnumber": "",
            "cc_belongstoinsured": data.paymentCategory === 'CreditCard' ? this.namedInsuredField?.value : '',
            "cc_receiptid": "",
            "chkdigit": "",
            "inscity": policyDetails?.insuredCity,
            "insstate": policyDetails?.insuredState,
            "inslob": policyDetails?.lob,
            "insuredaddress": policyDetails?.insuredAddress,
            "insuredname": "",
            "inszip": policyDetails?.insuredZip,
            "loginuser": "Guest",
            "paymentamt": Number(this.paymentOptionForm.value?.selectedAmtToPay.replace('$', '')),
            "paymentCategory": data.paymentCategory === 'CreditCard' ? 'CC' : 'EFT',
            "paymentstatus": "",
            "paymentstatuscomments": "",
            "payerfirstname": "",
            "payerlastname": "",
            "policystatus": policyDetails?.policyStatus,
            "quoteid": policyDetails?.quoteID,
            "policynumber": policyDetails?.policyNumber,
            "sessionid": data.sessionId,
            "s3receiptfilename": "",
            "sourcesystem": this.accountDetail?.policySource,
            "sourcesysupdated": "",
            "sourcesysupdatedcomments": "",
            "totalpaymentamount": Number(this.paymentOptionForm.value?.selectedAmtToPay.replace('$', '')),
            "transactionDate": this.datePipe.transform(new Date, 'yyyy-MM-dd', 'PDT'),
            "wkduedate": policyDetails?.paymentDue,
            "action": "charge",
            "primaryRatingState": policyDetails?.primaryRatingState,
            "tokenid": data.tokenId
        }
        this.callPaymentService(reqParam);
    }

    callPaymentService(reqParam: any) {
        let payload: IPayload = {} as IPayload;
        payload.encryptedData = encodeURIComponent(
            this.crypto.encryptData(reqParam, this.config.encryptKeyGPP)
        );
        this.paymentService.callPaymentService(payload, globalConstants.chargePayment).subscribe((res: UIResponse) => {
            if (res.statusCode === 200 && res.payload.chargePaymentResponse?.statusCode === 200) {
                this.router.navigate(['/payment-summary']);
                this.removeOneIncElement();
            } else {
                this.toastService.add({
                    type: 'error',
                    title: 'Error',
                    message: 'Your payment could not be processed.Please verify the payment information or choose a different payment method.',
                });
            }
        });
    }

    proceedPayment(event: any): void {
        if (event == 'goBack') {
            this.router.navigate(['/payment-center']);
        } else if (event == 'continue') {
            if (this.paymentMethod() === 'CreditCard' && this.processingFeeResponse?.eligible) {
                this.openCCPaymentDialog();
            } else if (this.paymentMethod() === 'ECheck' && this.accountDetail.eftMethod === 'Manual') {
                this.validateRoutingNumber();
            } else {
                this.proxy.Save();
            }
        }
    }

    validateRoutingNumber() {
        const reqParam = {
            RoutingNumber: this.eftpaymentForm.value?.routingNumber,
            action: globalConstants.validateRoutingNumber
        }
        let payload: IPayload = {} as IPayload;
        payload.encryptedData = encodeURIComponent(
            this.crypto.encryptData(reqParam, this.config.encryptKeyGPP)
        );
        this.paymentService.callPaymentService(payload, globalConstants.validateRoutingNumber).subscribe((res: UIResponse) => {
            if (res.statusCode === 200 && res.payload?.validateRoutingNumberResponse?.responseCode === 'Success') {
                this.eftpaymentForm.value.bankName = res.payload?.validateRoutingNumberResponse?.bankName || '';
                this.postLegacyEFTPayment();
            } else {
                this.toastService.add({
                    type: 'error',
                    title: 'Error',
                    message: res.payload?.validateRoutingNumberResponse?.responseMessage || 'Invalid Routing Number',
                });
            }
        });
    }

    postLegacyEFTPayment() {
        let firstName = '';
        let lastName = '';
        if (this.eftpaymentForm.valid) {
            let accountHolderName = this.eftpaymentForm.value.accountHolderName;
            if (accountHolderName.substr(0, accountHolderName.indexOf(' ')).length > 1) {
                firstName = accountHolderName.substr(0, accountHolderName.indexOf(' '));
                lastName = accountHolderName.substr(accountHolderName.indexOf(' ') + 1);
            } else {
                firstName = accountHolderName;
                lastName = '';
            }
        }
        const reqParam = {
            acctorpolicynumber: this.accountDetail?.policySource === 'Legacy' ? this.accountDetail?.legacyInPolicy : this.accountDetail?.accountNumber,
            policystatus: this.policyData.policyStatus,
            paymentamt: Number(this.paymentOptionForm.value?.selectedAmtToPay.replace('$', '')),
            transactionDate: this.datePipe.transform(new Date, 'yyyy-MM-dd', 'PDT'),
            transactionId: '',
            paymentCategory: 'EFT',
            eftMethod: 'Manual',
            acknowledgmentRequired: '',
            sourcesysupdated: '',
            sourcesysupdatedcomments: '',
            s3receiptfilename: '',
            sourcesystem: this.accountDetail.policySource,
            loginuser: 'Guest',
            insuredname: '',
            insuredaddress: this.policyData.insuredAddress,
            inscity: this.policyData.insuredCity,
            insstate: this.policyData.insuredState,
            inszip: this.policyData.insuredZip,
            agentreferences: '',
            agentphone: '',
            agentname: '',
            agentaddress: '',
            agentcity: '',
            agentstate: '',
            agentzip: '',
            cardholdername: '',
            cardholderzip: '',
            clientreferencedata1: '',
            cardexpirationyear: '',
            tokenid: '',
            timezone: '',
            cardtype: '',
            cardexpirationmonth: '',
            batchnumber: '',
            conveniencefee: '',
            totalpaymentamount: Number(this.paymentOptionForm.value?.selectedAmtToPay.replace('$', '')),
            cc_receiptid: '',
            lastfourdigits: '',
            cc_belongstoinsured: '',
            sessionid: '',
            authcode: '',
            paymentstatus: '',
            paymentstatuscomments: '',
            bankacctnumber: this.eftpaymentForm.value?.accountNumber || '',
            bankroutingnumber: this.eftpaymentForm.value?.routingNumber || '',
            bankname: this.eftpaymentForm.value?.bankName || '',
            bankaccttype: this.eftpaymentForm.value?.eftMethodType || '',
            payerfirstname: firstName,
            payerlastname: lastName,
            chkdigit: '',
            wkduedate: this.policyData.paymentDue,
            primaryRatingState: this.policyData.primaryRatingState
        }
        this.callPaymentService(reqParam);
    }

    openPaymentModal(event?: any): void {
        const popupCreditCard = {
            type: 'actionDialog',
            imageSrc: '../../../../assets/images/exclamation-octagon-orange.svg',
            message: 'Notice',
            messageExt: "By clicking 'Continue', you <br> acknowledge and provide your <br> consent for a third party payment <br> processor to complete your <br> transaction.",
            confirm: 'Continue',
            cancel: 'Cancel',
            fields: [],
        };

        this.modalService.confirm(popupCreditCard).subscribe((popupCallbackDetail) => {
            if (popupCallbackDetail[0] === 'Continue') {
                this.showProcessingFee.set(true);
                this.initiateOneIceSession();
            }
        })
    }

    getProcessingFee(): Observable<any> {
        let policyData = this.paymentDetails?.retrievePolicyResponse?.details.policyDetails?.at(0);
        let request = {
            action: globalConstants.getProcessingFee,
            paymentType: 'CARD',
            quoteid: policyData?.policyNumber,
            amount: (this.paymentOptionForm.value.selectedAmtToPay).replace('$', '') || "0.00",
            stateName: policyData?.primaryRatingState || policyData?.insuredState,
            lob: policyData?.lob || 'HO3',
        };
        this.loader.showLoading();
        return this.paymentService.getPaymentData(request).pipe(
            tap((response: any) => {
                this.loader.hideLoading();
                if (response.responseCode === 'Success') {
                    this.processingFeeResponse = response;
                } else {
                    this.toastService.add({
                        type: 'error',
                        title: 'Error',
                        message: 'Unable to retrieve processing fee information. Please try again later.',
                    });
                }
            })
        );
    }

    openCCPaymentDialog() {
        let dueToday = this.paymentOptionForm.value?.selectedAmtToPay.replace('$', '');
        let processingFee = this.processingFeeResponse?.convenienceFee;
        let totalAmountDue = (parseFloat(dueToday) + parseFloat(processingFee)).toFixed(2);
        let lob = this.paymentDetails?.retrievePolicyResponse?.details.policyDetails?.at(0)?.lob || '';
        let paymentDetails = {
            productType: new ProductNamePipe().transform(lob),
            totalPremium: '0000.00',
            dueToday: dueToday,
            processingFee: processingFee,
            totalAmountDue: totalAmountDue,
            oneIncDetails: '',
            state: this.policyData?.primaryRatingState || this.policyData?.insuredState || '',
        }

        this.dialogRef = this.dialog.open(CCPaymentConfirmationComponent, {
            data: { paymentDetails },
            maxWidth: '792px',
            panelClass: 'cc-payment-dialog',
            disableClose: true,
        });

        this.dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.proxy.Save();
            }
        });
    }

    numberCheck(event: any) {
        const charCode = (event.which) ? event.which : event.keyCode;
        if (charCode > 31 && (charCode < 48 || charCode > 57)) {
            return false;
        }
        return true;
    }

    removeOneIncElement() {
        let ele = document.getElementById('portalOneContainer');
        while (ele?.firstChild) {
            ele.removeChild(ele.firstChild);
        }
    }

    resetPaymentInformation() {
        this.showOneIncLogo.set(false);
        this.showNamedInsured.set(false);
        this.isReadyToSave.set(false);
        this.proceedpayFlag.set(false);
        this.showProcessingFee.set(false);
    }

    showSubmitButton() {
        if (this.paymentMethod() === 'ECheck' && this.accountDetail.eftMethod === 'Manual') {
            return this.paymentOptionForm?.valid && this.eftpaymentForm?.valid
        } else {
            return this.proceedpayFlag() && this.paymentOptionForm?.valid
        }
    }
}
